import React from 'react';
import PropTypes from 'prop-types';
import styled, { css as cssHelper } from 'styled-components';

import { isEmptyObject, isEqualObject, mediaHelper } from 'utils';
import Colors from 'constants/Colors';
import { Text, FontWeight } from '../../Text';
import RadioButton from './RadioButton';

const propTypes = {
  option: PropTypes.object.isRequired,
  tabIndex: PropTypes.number,
  alignTitleCenter: PropTypes.bool,
  renderBottomContent: PropTypes.func
};

const defaultProps = {
  alignTitleCenter: false,
  tabIndex: 0
};

const Title = styled(Text).attrs({
  weight: FontWeight.Bold
})`
  color: ${Colors.GRAY_DARKER};
  font-size: 14px;
`;

const TitleContainer = styled.div`
  ${mediaHelper.md`
    text-align: ${({ alignTitleCenter }) =>
      alignTitleCenter ? 'center' : 'initial'};
  `};
`;

const ContainerDescription = styled.div`
  flex: 1;
  width: 100%;
`;

const Description = styled(Text).attrs({
  weight: FontWeight.Bold
})`
  margin-top: 12px;
  font-size: 14px;
  line-height: 20px;
  display: block;
  color: ${Colors.GRAY};
`;

const Container = styled.div`
  border-radius: 8px;
  border: 1px solid ${Colors.GRAY_LIGHTER};
  padding: 16px;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  display: flex;
  flex-direction: row-reverse;

  ${({ disabled, selected }) => {
    if (disabled) {
      return cssHelper`
        border-color: ${selected ? Colors.GRAY_DARKEST : Colors.GRAY_LIGHTER};
        background: ${selected ? Colors.WHITE : 'rgba(240, 240, 240, 0.3)'};
        opacity: 0.3;
        cursor: not-allowed;

        & ${Title} {
          color: ${Colors.GRAY_DARKER};
        }

        & ${Description} {
          color: ${Colors.GRAY_DARKER};
        }
      `;
    }
    if (selected) {
      return cssHelper`
        border-color: ${Colors.PRIMARY_BLUE};
        box-shadow: 0px 3px 5px rgba(0, 0, 0, 0.1);

        & ${Title} {
          color: ${Colors.PRIMARY_BLUE};
        }

        & ${Description} {
          color: ${Colors.SECONDARY_DARKER};
        }
      `;
    }
    return '';
  }}

  ${mediaHelper.md`
    align-items: flex-start;
    flex-direction: column;
  `};
`;

const RadioButtonField = styled(RadioButton)`
  margin-right: 8px;
  justify-content: center;
  margin-top: 2px;

  ${mediaHelper.md`
    margin-top: 32px;
    align-self: center;
  `};
`;

const BottomContent = styled.div`
  width: 100%;
`;

const RadioCard = ({
  field,
  form,
  option,
  selected,
  disabled,
  tabIndex,
  alignTitleCenter,
  className,
  renderBottomContent,
  onClick,
  onChange,
  ...props
}) => {
  const isOptionEmpty = isEmptyObject(option);
  const { title, description } = option;
  const isSelected = !isOptionEmpty && isEqualObject(option.value, field.value);

  const _renderCard = () => (
    <Container
      className={!renderBottomContent && className}
      selected={isSelected}
      disabled={disabled}
      onClick={e => {
        e.stopPropagation();

        if (disabled) return;

        if (!isOptionEmpty) {
          form.setFieldValue(field.name, option.value);
          if (onChange) onChange(option.value);
        } else {
          onClick(field, form);
        }
      }}
    >
      <ContainerDescription>
        <TitleContainer alignTitleCenter={alignTitleCenter}>
          <Title>{title}</Title>
        </TitleContainer>
        {description && <Description>{description}</Description>}
      </ContainerDescription>
      <RadioButtonField
        name={field.name}
        option={option.value}
        tabIndex={tabIndex}
        field={field}
        form={form}
        disabled={isOptionEmpty || disabled}
        {...props}
      />
    </Container>
  );

  const _renderCardWithContent = () => (
    <div className={className}>
      {_renderCard()}

      <BottomContent>{isSelected && renderBottomContent()}</BottomContent>
    </div>
  );

  if (renderBottomContent) {
    return _renderCardWithContent();
  }

  return _renderCard();
};

RadioCard.propTypes = propTypes;
RadioCard.defaultProps = defaultProps;

export default RadioCard;
